// Pre-policy month builder. A policy cannot pay for care delivered before it
// exists, so any care month whose policy year is < 1 (care year before
// `policyPremiumStartYear`) books the full cost as out-of-pocket and leaves
// every carry-forward balance untouched.
//
// Strategies call this from the top of `computeMonth` — before any
// WP/ADL classification or payout math — so a pre-policy month never draws the
// shared pool, consumes the elimination period, or reduces the death benefit.
// On joint policies this is what keeps an early-claiming partner's pre-policy
// months from draining the pool a year early and corrupting the client's
// downstream numbers.
//
// The returned entry is uniform across products: zero payout (full cost OOP),
// zero pool/EOB/DB flows, `state === openState` (pools, WP, strategyState all
// carried forward unchanged), and a `'pre-policy'` ineligibility reason with
// the whole month booked as `prePolicyDays`. The strategy passes its own zeroed
// `strategyDeltas` so the row's deltas shape matches its in-force rows.

import { DAYS_IN_MONTH } from './sharedConstants';
import { EMPTY_INELIGIBLE_DAY_BREAKDOWN } from './deriveIneligibilityReason';

export interface BuildPrePolicyMonthInput<TState = unknown, TDeltas = unknown> {
  context: MonthContext;
  facts: MonthlyCareFacts;
  openState: MonthlyState<TState>;
  strategyDeltas: TDeltas;
}

export function buildPrePolicyMonth<TState, TDeltas>(
  input: BuildPrePolicyMonthInput<TState, TDeltas>,
): MonthlyEntry<TState, TDeltas> {
  const { context, facts, openState, strategyDeltas } = input;

  const payout: PayoutSummary = {
    ltcCoveragePaid: 0,
    ltcPayoutToCost: 0,
    ineligibleCost: facts.effectiveCareCost,
    indemnityPayout: 0,
  };

  const deltas: MonthlyDeltas<TDeltas> = {
    payout,
    base: { depletion: 0, inflationAccrual: 0 },
    eob: { depletion: 0, inflationAccrual: 0 },
    deathBenefit: { reduction: 0, flooredAtMinimum: false },
    strategyDeltas,
  };

  // No drawdown this month, so pool-exhaustion mirrors the opening balances;
  // the elimination period is untouched, so it is not yet satisfied.
  const ltcPoolRemaining =
    openState.pools.baseBenefitPool + openState.pools.extensionOfBenefitsPool;
  const status: MonthlyStatus = {
    ltcPoolExhausted: ltcPoolRemaining <= 0,
    clientPoolExhausted: ltcPoolRemaining <= 0,
    epSatisfied: false,
  };

  return {
    context,
    facts,
    openState,
    state: openState,
    deltas,
    events: {
      status,
      milestones: [],
      ineligibilityReason: 'pre-policy',
      ineligibleDayBreakdown: {
        ...EMPTY_INELIGIBLE_DAY_BREAKDOWN,
        prePolicyDays: DAYS_IN_MONTH,
      },
    },
  };
}
